import React, { useState, useRef, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { TERMS_VERSION } from '../constants/legal';
import Icon from './AppIcon';
import Button from './ui/Button';
import TermsPrivacyModal from './TermsPrivacyModal';

// Routes where a consent prompt would get in the way of the auth flow itself.
const HIDE_ON = ['/reset-password', '/forgot-password', '/check-email'];

/**
 * Re-consent banner (Law 18-07). Shown to any logged-in user who hasn't agreed
 * to the CURRENT Terms/Privacy version — i.e. users who registered before the
 * consent flow existed, or who signed up via Google OAuth (which skips the
 * registration checkbox). Accepting records consent in their auth metadata.
 */
const ReConsentBanner = () => {
  const { user } = useAuth();
  const { t } = useTranslation('auth');
  const location = useLocation();
  const [showTerms, setShowTerms] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const barRef = useRef(null);

  const accepted = user?.user_metadata?.terms_version === TERMS_VERSION;
  const shouldShow =
    !!user && !accepted && !done && !HIDE_ON?.includes(location?.pathname);

  /**
   * The bar is position:fixed so it stays visible (it's a consent gate, not a
   * dismissible toast) — but fixed elements don't occupy layout, so it was
   * covering page content on every route: table rows, the landing hero's
   * primary CTA, dashboard actions. Reserving matching space at the bottom of
   * the document keeps it prominent without hiding anything behind it.
   */
  useLayoutEffect(() => {
    if (!shouldShow) {
      document.body.style.removeProperty('padding-bottom');
      return undefined;
    }
    const el = barRef.current;
    if (!el) return undefined;

    const apply = () => {
      // Corner card: reserve only a small gutter so it can't sit on top of
      // content at the very bottom of a scrolled page.
      document.body.style.setProperty('padding-bottom', '5rem');
    };
    apply();

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    ro?.observe(el);
    window.addEventListener('resize', apply);

    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', apply);
      document.body.style.removeProperty('padding-bottom');
    };
  }, [shouldShow]);

  if (!shouldShow) return null;

  const handleAgree = async () => {
    try {
      setSaving(true);
      const { error } = await supabase?.auth?.updateUser({
        data: {
          terms_accepted_at: new Date()?.toISOString(),
          terms_version: TERMS_VERSION
        }
      });
      if (!error) setDone(true);
    } catch (e) {
      // Non-fatal: keep the banner up so the user can retry.
      console.error('Consent update failed:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div ref={barRef} className="fixed bottom-3 inset-inline-end-3 z-[110] max-w-[min(26rem,calc(100vw-1.5rem))]" style={{ insetInlineEnd: '0.75rem' }}>
        <div className="rounded-lg border border-border bg-card shadow-elevation-4 p-3.5 flex flex-col gap-3">
          <div className="flex items-start gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="ShieldCheck" size={15} className="text-primary" />
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t('legal.banner.message')}
            </p>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTerms(true)}
              iconName="FileText"
              iconPosition="left"
            >
              {t('legal.banner.read')}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleAgree}
              loading={saving}
              iconName="Check"
              iconPosition="left"
            >
              {t('legal.banner.agree')}
            </Button>
          </div>
        </div>
      </div>

      {/* Reading and accepting are the same intent, so the modal can close the
          loop rather than sending the person back to hunt for the banner. */}
      <TermsPrivacyModal
        open={showTerms}
        onClose={() => setShowTerms(false)}
        accepting={saving}
        onAccept={async () => {
          await handleAgree();
          setShowTerms(false);
        }}
      />
    </>
  );
};

export default ReConsentBanner;
